/**
 * RESPONSIBILITY: arrange one row's columns into the panels they declare.
 *
 * A column naming a `group` joins a collapsible panel, exactly as a top-level field does
 * — see schema-gen/ui-schema.ts, which does the same job for the section form. It did
 * NOT happen here until now: `group` was read at section level only, so a @Row that
 * declared panels got a flat wall of inputs and the annotation quietly meant nothing.
 * Twenty-four columns on one line is not a form anybody fills in twice.
 *
 * The panel is created by the first column that names it, which is also the column that
 * decides whether it starts closed. Ungrouped columns keep their place in the order they
 * were declared, so a row that names no group produces exactly the row it always did.
 */
import type { ItemField } from './cell-types';
import { RowCell } from './row-cell';
import { visible } from './show-when';

export interface RowFieldsProps {
  /** Unique within the form. Cell ids are built from it. */
  path: string;
  index: number;
  fields: ItemField[];
  row: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  /** The section document. A cell with `in` resolves its vocabulary against this. */
  root: Record<string, unknown>;
  /** Fixed vocabularies out of the JSON Schema, by column key. */
  enums: Record<string, string[] | undefined>;
}

export function RowFields({ path, index, fields, row, onChange, root, enums }: RowFieldsProps) {
  const shown = fields.filter((f) => visible(f, row));

  const cellFor_ = (f: ItemField) => (
    <RowCell
      key={f.key} field={f} index={index} row={row} root={root}
      id={`${path}.${index}.${f.key}`}
      // A fixed vocabulary reaches a NESTED column on the annotation rather than in the
      // schema, which a cell this far down cannot see.
      enumVals={enums[f.key] ?? (f.fixed as string[] | undefined)}
      onChange={(v) => onChange(f.key, v)}
    />
  );

  /* Panels in declaration order, each holding its own columns. A Map keeps insertion
     order, so the panel appears where its first column would have. */
  const panels = new Map<string, { open: boolean; cells: JSX.Element[] }>();
  const flow: (JSX.Element | string)[] = [];

  for (const f of shown) {
    if (!f.group) { flow.push(cellFor_(f)); continue; }

    let panel = panels.get(f.group);
    if (!panel) {
      // The column that CREATES the panel decides whether it starts open.
      panel = { open: !f.collapsed, cells: [] };
      panels.set(f.group, panel);
      flow.push(f.group);
    }
    panel.cells.push(cellFor_(f));
  }

  return (
    <div className="ctl-list-fields">
      {flow.map((item) => {
        if (typeof item !== 'string') return item;
        const panel = panels.get(item)!;
        return (
          <details key={item} className="ctl-group ctl-cell-group" open={panel.open}>
            <summary className="ctl-group-head">{item}</summary>
            <div className="ctl-group-body ctl-list-fields">{panel.cells}</div>
          </details>
        );
      })}
    </div>
  );
}
